import { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';
import { toast } from 'sonner';
import { EMPLOYEE_DOCS } from '../employees/constants';
import { PermitDocList } from './PermitDocList';
import { StoreObjectionBox } from './StoreObjectionBox';
import { StarScore } from '../five-star/stars';
import { PersonName } from '../employees/BlockEmployeeDialog';
import { snapshotCompany, snapshotList } from '../../lib/permitSnapshot';
import { COMPANY_DOCS } from '../companies/constants';
import { 
  FileText, 
  Download, 
  CheckCircle, 
  XCircle, 
  Users, 
  Truck, 
  Wrench, 
  History, 
  User,
  MapPin,
  Building2,
  Calendar,
  Clock,
  Briefcase,
  Siren,
  ClipboardList,
  AlertTriangle,
  Shield
} from 'lucide-react';
import { 
  formatDate, 
  maskTcNo,
  getActualStatus,
  getStatusLabel,
  getAuditActionLabel, getRoleLabel,
  getAuditActionIcon,
  formatAuditTimestamp,
  WORK_PERMIT_TYPES
} from './constants';

const WorkPermitDetailDialog = ({
  open,
  onOpenChange,
  permit,
  getLocationName,
  getCompanyName,
  allEmployees,
  allEquipment,
  company: companyLive,
  offline = false,
  onChanged,
  starScore,
  empScores,
  lowScore = 2.5
}) => {
  const [downloading, setDownloading] = useState(false);

  if (!permit) return null;

  // Status badge helper
  const getStatusBadge = (status, permitData = null) => {
    const actualStatus = permitData ? getActualStatus(permitData) : status;
    const statusConfig = {
      'aktif': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Onaylandı' },
      'taslak': { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Taslak', icon: FileText },
      'eksik_hatali': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Eksik/Hatalı', icon: AlertTriangle },
      'talep_bilgisi_hatali': { bg: 'bg-fuchsia-100', text: 'text-fuchsia-700', label: 'Talep Bilgisi Hatalı', icon: AlertTriangle },
      'onay_bekliyor': { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Onay Bekliyor', icon: Clock },
      'red_edildi': { bg: 'bg-rose-100', text: 'text-rose-700', label: 'Reddedildi', icon: XCircle },
      'geri_cekildi': { bg: 'bg-slate-200', text: 'text-slate-600', label: 'Geri Çekildi', icon: XCircle },
      'suresi_dolmus': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Süresi Dolmuş' },
      'tamamlandi': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Tamamlandı' },
      'iptal': { bg: 'bg-rose-100', text: 'text-rose-700', label: 'İptal' }
    };
    const config = statusConfig[actualStatus] || { bg: 'bg-slate-100', text: 'text-slate-700', label: actualStatus };
    const Icon = config.icon;
    return (
      <Badge className={`${config.bg} ${config.text} flex items-center gap-1`}>
        {Icon && <Icon className="h-3 w-3" />}
        {config.label}
      </Badge>
    );
  };

  // PDF Download handler
  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/work-permits/${permit.id}/download-deficiency-pdf`,
        {
          responseType: 'blob',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `eksiklik_raporu_${permit.request_number || permit.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF başarıyla indirildi');
    } catch (error) {
      console.error('PDF indirme hatası:', error);
      toast.error('PDF indirilemedi');
    } finally {
      setDownloading(false);
    }
  };

  // Get employees for this permit
  const permitEmployees = snapshotList(permit, allEmployees.filter(e => permit.employee_ids?.includes(e.id)), 'employees')
    .map(e => (permit.employee_info?.[e.id] ? { ...e, ...permit.employee_info[e.id] } : e));
  const company = snapshotCompany(permit, companyLive);
  
  // Get equipment for this permit
  const permitEquipment = snapshotList(permit, allEquipment.filter(eq => permit.equipment_ids?.includes(eq.id)), 'equipment');

  // Get work permit type labels
  const workPermitTypeLabels = (permit.work_permit_types || [])
    .map(typeId => WORK_PERMIT_TYPES.find(t => t.id === typeId)?.label)
    .filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" data-testid="work-permit-detail-dialog">
        <DialogHeader className="border-b pb-4 flex-shrink-0">
          <DialogTitle className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <FileText className="h-5 w-5 text-slate-700" />
            </div>
            <div>
              <span className="text-lg">Çalışma İzni Detayları</span>
              {permit.request_number && (
                <Badge variant="outline" className="ml-3 font-mono">{permit.request_number}</Badge>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="details" className="mt-3 flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="details" className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4" />
              Detaylar
            </TabsTrigger>
            <TabsTrigger value="timeline" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              Talep Akışı
            </TabsTrigger>
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details" className="flex-1 overflow-y-auto pr-2">
            <div className="space-y-5">
              {/* Basic Info Card */}
              <div className="bg-gradient-to-r from-slate-50 to-slate-100/50 rounded-xl p-4 border border-slate-200">
                <div className="grid grid-cols-2 gap-4">
                  <InfoItem 
                    icon={<MapPin className="h-4 w-4 text-blue-600" />}
                    label="İşletme"
                    value={getLocationName(permit.location_id)}
                  />
                  <InfoItem 
                    icon={<Building2 className="h-4 w-4 text-purple-600" />}
                    label="Firma"
                    value={getCompanyName(permit.company_id)}
                  />
                </div>
              </div>

              {/* Job Description */}
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-amber-50 rounded-lg">
                    <Briefcase className="h-4 w-4 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Yapılacak İş</Label>
                    <p className="font-medium text-slate-800 mt-1">{permit.job_description}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 border border-slate-200" data-testid="detail-special-situations">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-amber-50 rounded-lg"><Siren className="h-4 w-4 text-amber-600" /></div>
                  <div className="flex-1">
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Çalışmaya Bağlı Özel Durumlar</Label>
                    {permit.special_situations?.length ? (
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {permit.special_situations.map(s => <Badge key={s.id} className="bg-amber-100 text-amber-800 hover:bg-amber-100 font-medium" title={s.description || ''}>{s.title}</Badge>)}
                      </div>
                    ) : <p className="text-sm italic text-slate-500 mt-1">Çalışmaya bağlı özel durum belirtilmemiştir</p>}
                  </div>
                </div>
              </div>

              {/* Date & Time Card */}
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="grid grid-cols-2 gap-4">
                  <InfoItem 
                    icon={<Calendar className="h-4 w-4 text-emerald-600" />}
                    label="Tarih Aralığı"
                    value={`${formatDate(permit.start_date)} - ${formatDate(permit.end_date)}`}
                  />
                  <InfoItem 
                    icon={<Clock className="h-4 w-4 text-indigo-600" />}
                    label="Çalışma Saatleri"
                    value={`${permit.start_time} - ${permit.end_time}`}
                  />
                </div>
              </div>

              {starScore?.avg < lowScore && (
                <div className="flex flex-wrap items-center gap-3 rounded-xl border border-rose-300 bg-rose-100 p-4 text-sm text-rose-800" data-testid="detail-low-score">
                  <AlertTriangle className="h-5 w-5 shrink-0" />
                  <span className="font-semibold">Dikkat: Bu firmanın Five Star İSG puanı düşük.</span>
                  <StarScore value={starScore.avg} jobs={starScore.jobs} lowData={starScore.low_data} />
                  <span className="text-xs">Sahada ek kontrol ve denetim önerilir.</span>
                </div>
              )}
              {permit.store_objection && !offline && <StoreObjectionBox key={permit.id} permit={permit} onChanged={onChanged} />}

              {/* İş Tipi & İş Çeşidi */}
              {(permit.is_tipi || permit.is_cesidi) && (
                <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-100">
                  <div className="grid grid-cols-2 gap-4">
                    {permit.is_tipi && (
                      <InfoItem 
                        icon={<Briefcase className="h-4 w-4 text-blue-600" />}
                        label="İş Tipi"
                        value={permit.is_tipi}
                        badge
                        badgeClass="bg-blue-100 text-blue-700"
                      />
                    )}
                    {permit.is_cesidi && (
                      <InfoItem 
                        icon={<Briefcase className="h-4 w-4 text-purple-600" />}
                        label="İş Çeşidi"
                        value={permit.is_cesidi}
                        badge
                        badgeClass="bg-purple-100 text-purple-700"
                      />
                    )}
                    {permit.store_name && (
                      <InfoItem
                        icon={<Briefcase className="h-4 w-4 text-amber-600" />}
                        label="Çalışılacak Mağaza/İşletme"
                        value={permit.store_name}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Work Permit Types */}
              {workPermitTypeLabels.length > 0 && (
                <div className="bg-amber-50/50 rounded-xl p-4 border border-amber-100">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <Shield className="h-4 w-4 text-amber-700" />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs text-slate-500 uppercase tracking-wider">İş İzni Türleri</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {workPermitTypeLabels.map((label, idx) => (
                          <Badge key={idx} className="bg-amber-100 text-amber-700 border border-amber-200">
                            {label}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Status */}
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-slate-500 uppercase tracking-wider">Durum</Label>
                  {getStatusBadge(permit.status, permit)}
                </div>
                {permit.status === 'red_edildi' && permit.rejection_reason && (
                  <div className="mt-3 p-3 bg-rose-50 border border-rose-200 rounded-lg">
                    <p className="text-sm font-medium text-rose-700">Red Açıklaması</p>
                    <p className="text-sm text-rose-600 mt-1">{permit.rejection_reason}</p>
                  </div>
                )}
              </div>

              {/* Firma Belgeleri */}
              {company && (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="permit-detail-company-docs">
                  <div className="bg-purple-50 px-4 py-3 border-b border-purple-100 flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-purple-600" />
                    <span className="font-medium text-purple-800">Firma Belgeleri – {company.name}</span>
                  </div>
                  <div className="p-3 [&>div]:ml-0 [&>div]:mt-0">
                    <PermitDocList entity={company} entityType="companies" docs={COMPANY_DOCS} title={company.name} />
                  </div>
                </div>
              )}

              {/* Personnel Section */}
              {permitEmployees.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-100 flex items-center gap-2">
                    <Users className="h-4 w-4 text-emerald-600" />
                    <span className="font-medium text-emerald-800">
                      Çalışacak Personel ({permitEmployees.length} kişi)
                    </span>
                  </div>
                  <div className="p-4 space-y-2">
                    {permitEmployees.map(emp => (
                      <PersonnelCard 
                        key={emp.id} 
                        employee={emp} 
                        task={permit.employee_tasks?.[emp.id]} 
                        permitEndDate={permit.end_date}
                        star={empScores?.[emp.id]}
                        lowScore={lowScore}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Equipment Section */}
              {permitEquipment.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                    <Wrench className="h-4 w-4 text-slate-600" />
                    <span className="font-medium text-slate-800">
                      Kullanılacak Ekipmanlar ({permitEquipment.length})
                    </span>
                  </div>
                  <div className="p-4 space-y-3">
                    {permitEquipment.map(eq => (
                      <EquipmentCard key={eq.id} equipment={eq} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline" className="flex-1 overflow-y-auto pr-2">
            <div className="py-2">
              {permit.audit_log?.length > 0 ? (
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-200" />
                  
                  <div className="space-y-4">
                    {[...permit.audit_log].reverse().map((entry, idx) => (
                      <div key={idx} className="relative flex gap-4 pl-2">
                        {/* Timeline dot */}
                        <div className="relative z-10 flex items-center justify-center w-7 h-7 bg-white border-2 border-slate-300 rounded-full text-sm">
                          {getAuditActionIcon(entry.action)}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 bg-slate-50 rounded-xl p-4 border border-slate-200 hover:border-slate-300 transition-colors">
                          <div className="flex justify-between items-start">
                            <span className="font-medium text-slate-800">{getAuditActionLabel(entry.action)}</span>
                            <span className="text-xs text-slate-500 bg-white px-2 py-1 rounded-md">
                              {formatAuditTimestamp(entry.timestamp)}
                            </span>
                          </div>
                          {entry.user_name && (
                            <div className="text-sm text-slate-600 flex items-center gap-2 mt-2">
                              <User className="h-3.5 w-3.5" />
                              <span>{entry.user_name}</span>
                              {entry.user_role && (
                                <Badge variant="outline" className="text-[10px] h-5">{getRoleLabel(entry.user_role)}</Badge>
                              )}
                            </div>
                          )}
                          {entry.details && (
                            <p className="text-sm text-slate-500 mt-2 bg-white p-2 rounded-lg">{entry.details}</p>
                          )}
                          {entry.old_status && entry.new_status && (
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline" className="text-xs">{getStatusLabel(entry.old_status)}</Badge>
                              <span className="text-slate-400">→</span>
                              <Badge className="bg-slate-700 text-xs">{getStatusLabel(entry.new_status)}</Badge>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto bg-slate-100 rounded-full flex items-center justify-center mb-4">
                    <History className="h-8 w-8 text-slate-400" />
                  </div>
                  <p className="text-slate-600 font-medium">Henüz akış kaydı bulunmuyor</p>
                  <p className="text-sm text-slate-400 mt-1">Yeni talepler otomatik kayıt altına alınır</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="border-t pt-4 mt-auto flex-shrink-0 flex gap-2">
          {!offline && <Button 
            variant="outline" 
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="gap-2"
            data-testid="download-deficiency-pdf-btn"
          >
            <Download className="h-4 w-4" />
            {downloading ? 'İndiriliyor...' : 'Eksiklik Raporu PDF'}
          </Button>}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Kapat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Helper Components
const InfoItem = ({ icon, label, value, badge = false, badgeClass = '' }) => (
  <div className="flex items-start gap-3">
    <div className="p-2 bg-white rounded-lg shadow-sm border border-slate-100">
      {icon}
    </div>
    <div className="flex flex-col items-start gap-1.5">
      <Label className="text-xs text-slate-500 uppercase tracking-wider">{label}</Label>
      {badge ? (
        <Badge className={badgeClass}>{value}</Badge>
      ) : (
        <p className="font-medium text-slate-800">{value}</p>
      )}
    </div>
  </div>
);

const PersonnelCard = ({ employee, task, permitEndDate, star, lowScore }) => {
  const docs = employee?.documents || {};
  const missingDocs = [];
  const pendingDocs = [];
  const expiringDocs = [];
  
  // Belge durumu kontrolü
  const docChecks = EMPLOYEE_DOCS.map(d => ({
    key: d.key,
    label: d.key === 'mesleki_yeterlilik' ? 'Mesleki Yeterlilik (MYK)' : d.label,
    hasValidity: ['isg_egitimi', 'saglik_raporu', 'mesleki_yeterlilik'].includes(d.key),
    altDateField: d.key === 'mesleki_yeterlilik' ? 'mesleki_yeterlilik_date' : undefined,
  }));
  
  // Tarihleri UTC olarak parse et (timezone sorunu yaşamadan)
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    if (typeof dateStr === 'string' && dateStr.includes('-')) {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(Date.UTC(year, month - 1, day));
    }
    return new Date(dateStr);
  };
  
  // Tarih formatla: DD/MM/YYYY
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '-';
    if (typeof dateStr === 'string' && dateStr.includes('-')) {
      const [year, month, day] = dateStr.split('-');
      if (year && month && day) {
        return `${day}/${month}/${year}`;
      }
    }
    return dateStr;
  };
  
  docChecks.forEach(doc => {
    const status = docs[doc.key];
    // Sadece hasValidity: true olan belgeler için geçerlilik kontrolü yap
    // MYK için _validity veya _date alanını kontrol et
    let validityDate = doc.hasValidity ? docs[`${doc.key}_validity`] : null;
    if (!validityDate && doc.altDateField) {
      validityDate = docs[doc.altDateField];
    }
    
    if (!status || status === 'beklemede') {
      if (employee?.document_files?.[doc.key]) pendingDocs.push(doc.label);
    } else if (status !== 'onay' && status !== 'kapsam_disi') {
      missingDocs.push({ label: doc.label, note: docs[`${doc.key}_note`] || (status === 'suresi_doldu' ? 'Süresi doldu' : 'Eksik') });
    } else if (status === 'onay' && doc.hasValidity && validityDate && permitEndDate) {
      // Talep tarih aralığında süresi dolacak mı kontrol et
      try {
        const docExpiry = parseDate(validityDate);
        const permitEnd = parseDate(permitEndDate);
        if (docExpiry && permitEnd && docExpiry < permitEnd) {
          expiringDocs.push({ label: doc.label, date: formatDateDisplay(validityDate) });
        }
      } catch (e) {
        // Tarih parse hatası - yoksay
      }
    }
  });
  
  const hasDeficiency = missingDocs.length > 0;
  const hasPending = !hasDeficiency && pendingDocs.length > 0;
  const hasExpiring = !hasPending && expiringDocs.length > 0;

  return (
    <div className={`p-3 rounded-lg border transition-colors ${
      hasDeficiency 
        ? 'border-rose-200 bg-rose-50 hover:bg-rose-100' 
        : hasPending
          ? 'border-amber-200 bg-amber-50 hover:bg-amber-100'
        : hasExpiring
          ? 'border-amber-200 bg-amber-50 hover:bg-amber-100'
          : 'border-emerald-200 bg-emerald-50 hover:bg-emerald-100'
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {hasDeficiency ? (
            <XCircle className="h-4 w-4 text-rose-500" />
          ) : hasPending ? (
            <Clock className="h-4 w-4 text-amber-500" />
          ) : hasExpiring ? (
            <AlertTriangle className="h-4 w-4 text-amber-500" />
          ) : (
            <CheckCircle className="h-4 w-4 text-emerald-500" />
          )}
          <PersonName person={employee} className="font-medium text-slate-800" />
          {employee.is_blocked && <span className="rounded bg-rose-100 px-1.5 text-[10px] font-semibold text-rose-700">Bloklanmıştır</span>}
          {employee.is_archived && <span className="rounded bg-slate-100 px-1.5 text-[10px] font-semibold text-slate-500">Silinmiştir</span>}
          {employee.position && (
            <Badge variant="outline" className="text-xs">{employee.position}</Badge>
          )}
          {star?.gold && <span className="inline-flex items-center gap-0.5 rounded-full border border-yellow-500 bg-gradient-to-b from-yellow-200 via-yellow-400 to-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-amber-950" title={`Five Star: ${star.avg} / 5 (${star.jobs} iş)`} data-testid={`permit-detail-emp-gold-${employee.id}`}>★ Altın Personel</span>}
          {star?.avg < lowScore && <Badge className="bg-rose-600 text-white hover:bg-rose-600 text-[10px]" title={`Five Star: ${star.avg} / 5 (${star.jobs} iş)`} data-testid={`permit-detail-emp-low-${employee.id}`}>Düşük puan {star.avg}</Badge>}
        </div>
        <Badge className={
          hasDeficiency 
            ? 'bg-rose-100 text-rose-700' 
            : hasPending || hasExpiring 
              ? 'bg-amber-100 text-amber-700'
              : 'bg-emerald-100 text-emerald-700'
        } data-testid={`permit-detail-emp-status-${employee.id}`}>
          {hasDeficiency ? 'Eksik' : hasPending ? 'Onay Bekliyor' : hasExpiring ? 'Süresi Dolacak' : 'Onaylı'}
        </Badge>
      </div>
      {employee.tc_kimlik_no && (
        <p className="text-slate-500 text-xs mt-1 ml-6 font-mono">{employee.nationality === 'yabanci' ? `Pasaport: ${employee.tc_kimlik_no}` : `T.C.: ${maskTcNo(employee.tc_kimlik_no)}`}</p>
      )}
      {task && (
        <p className="text-xs mt-1 ml-6 text-indigo-600">
          <span className="font-medium">Görev:</span> {task}
        </p>
      )}
      {hasDeficiency && (
        <div className="mt-2 ml-6 text-xs text-rose-600" data-testid={`permit-detail-emp-missing-${employee.id}`}>
          <span className="font-medium">Eksik belgeler:</span>
          <ul className="mt-1 space-y-0.5">
            {missingDocs.map((d, i) => (
              <li key={i} className="break-words">• <span className="font-medium text-slate-700">{d.label}:</span> {d.note}</li>
            ))}
          </ul>
        </div>
      )}
      {pendingDocs.length > 0 && (
        <div className="mt-1 ml-6 text-xs text-amber-700">
          <span className="font-medium">Onay bekleyen: </span>{pendingDocs.join(', ')}
        </div>
      )}
      {hasExpiring && (
        <div className="mt-2 ml-6 text-xs text-amber-700 bg-amber-100/50 p-2 rounded">
          <span className="font-medium flex items-center gap-1">
            <AlertTriangle className="h-3 w-3 flex-shrink-0" />
            <span>Talep tarih aralığında süresi dolacak belgeler:</span>
          </span>
          <ul className="mt-1 space-y-0.5">
            {expiringDocs.map((doc, idx) => (
              <li key={idx} className="break-words">• {doc.label} - Geçerlilik: {doc.date}</li>
            ))}
          </ul>
        </div>
      )}
      <PermitDocList entity={employee} docs={EMPLOYEE_DOCS} title={`${employee.name} ${employee.surname}`} />
    </div>
  );
};

const EquipmentCard = ({ equipment }) => {
  const isVehicle = equipment.equipment_type === 'arac';
  const equipmentDocs = isVehicle 
    ? [
        { key: 'operatorluk_belgesi', label: 'Operatörlük Belgesi' },
        { key: 'periyodik_muayene', label: 'Periyodik Muayene' },
        { key: 'mali_mesuliyet_sigortasi', label: 'Mali Mesuliyet Sigortası' },
      ]
    : [
        { key: 'periyodik_muayene', label: 'Periyodik Muayene' },
      ];

  return (
    <div className="p-3 border rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
      <div className="flex items-center gap-2 mb-3">
        <div className={`p-1.5 rounded-md ${isVehicle ? 'bg-blue-100' : 'bg-amber-100'}`}>
          {isVehicle ? (
            <Truck className="h-4 w-4 text-blue-600" />
          ) : (
            <Wrench className="h-4 w-4 text-amber-600" />
          )}
        </div>
        <span className="font-medium text-slate-800">{equipment.name}</span>
        <Badge variant="outline" className="text-xs">
          {isVehicle ? 'Araç' : 'İş Ekipmanı'}
        </Badge>
      </div>
      <PermitDocList entity={equipment} entityType="equipment" docs={equipmentDocs} title={equipment.name} />
    </div>
  );
};

export default WorkPermitDetailDialog;
